import React from 'react';
import { IGraphData, IGraphDataGender } from '../../../../interfaces/types';
import { Trans } from '@lingui/macro';
import { useMemo, useState } from 'react';
import { EStatisticsInterval } from '../../../../../reducers/data';
import moment from 'moment';
import { store } from '../../../../../store';
import { numberWithSpaces } from '../../../../helpers/index';

interface IProps {
    graphResults?: (IGraphDataGender | IGraphData)[],
    filter?: string,
    interval?: EStatisticsInterval,
    isGender?: boolean
}

interface ICell {
    name: string,
    count: string,
    from: string
}

type SortCol = 'label' | number
type SortDir = 'asc' | 'desc'

const OccupancyTableGraph = ({graphResults, filter, interval, isGender}: IProps) => {

    const [sortCol, setSortCol] = useState<SortCol>('label')
    const [sortDir, setSortDir] = useState<SortDir>('asc')

    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortCol(col)
            setSortDir(col === 'label' ? 'asc' : 'desc')
        }
    }

    const SortIcon = ({ col }: { col: SortCol }) => {
        if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>
        return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
    }

    // Collect all location/zone names in display order
    const allNames: string[] = useMemo(() => {
        const names: string[] = []
        graphResults?.forEach(r => {
            if (!names.includes(r.name)) names.push(r.name)
        })
        return names
    }, [graphResults])

    const splitted = useMemo(() => {
        let splittedVal: {
            start: string
            values: ICell[]
        }[] = []

        graphResults?.forEach(r => {
            r.data.forEach(d => {
                let was = false

                splittedVal.forEach(s => {
                    if (s.start === d.start) {
                        was = true
                        s.values.push({
                            count: d.count,
                            name: r.name,
                            from: d.start
                        })
                    }
                })

                if (!was) {
                    splittedVal.push({
                        start: d.start,
                        values: [{
                            count: d.count,
                            name: r.name,
                            from: d.start
                        }]
                    })
                }
            })
        })

        // Ensure every time row has a cell for every location;
        // fill missing ones with "N/A" so columns stay aligned.
        splittedVal.forEach(s => {
            const filled: ICell[] = allNames.map(name => {
                const existing = s.values.find(v => v.name === name)
                return existing ?? { count: 'N/A', name, from: s.start }
            })
            s.values = filled
        })

        return splittedVal
    }, [graphResults, allNames])

    // Sorted rows
    const sortedRows = useMemo(() => {
        return [...splitted].sort((a, b) => {
            let aVal: number | string
            let bVal: number | string
            if (sortCol === 'label') {
                aVal = a.start
                bVal = b.start
            } else {
                const aCell = a.values[sortCol as number]
                const bCell = b.values[sortCol as number]
                aVal = aCell?.count === 'N/A' ? -1 : Number(aCell?.count ?? 0)
                bVal = bCell?.count === 'N/A' ? -1 : Number(bCell?.count ?? 0)
            }
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
            return 0
        })
    }, [splitted, sortCol, sortDir])

    // Column averages (only shown when >1 row; sum is meaningless for occupancy snapshots)
    const columnAvgs: (number | null)[] = useMemo(() => {
        return allNames.map((_, ci) => {
            const validValues = splitted
                .map(s => s.values[ci])
                .filter(v => v && v.count !== 'N/A')
                .map(v => Number(v!.count))
            if (validValues.length === 0) return null
            return validValues.reduce((acc, n) => acc + n, 0) / validValues.length
        })
    }, [splitted, allNames])

    const showSum = splitted.length > 1

    let urlExport = "https://api.clientportal.visitor.sk/occupancy/?id=" + filter + "&token=" + store.getState().userStore.token

    return (
        <div className="table_scroll mgt-20 pdb-40">
            <div className="pdb-10">
                <a href={urlExport} className="btn_action text_nowrap_download text_w500">
                    <svg className="sico wh16"><use xlinkHref="/assets/layout/icons.svg#s_dl"></use></svg>
                    <span><Trans>Stiahnuť tabuľku</Trans></span>
                </a>
            </div>
            <table className="table">
                <thead>
                    <tr>
                        <th
                            onClick={() => handleSort('label')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            <Trans>Dátum - časový interval</Trans>
                            <SortIcon col="label" />
                        </th>
                        {allNames.map((name, i) => (
                            <th
                                key={i}
                                onClick={() => handleSort(i)}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                {name}
                                <SortIcon col={i} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((s, i) => (
                        <tr key={i}>
                            <td>{moment(s.start).format('DD. MM. YYYY HH:mm:ss')}</td>
                            {s.values.map((v, j) => (
                                <td key={j}>
                                    {v.count === 'N/A' ? (
                                        <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                                    ) : (
                                        <div className="table_drop">
                                            <span>{numberWithSpaces(Number(v.count))}</span>
                                            <div className="drop">
                                                <div><Trans>Názov</Trans>: <strong>{v.name}</strong></div>
                                                <div><Trans>Deň</Trans>: <strong>{moment(v.from).format('dddd')}</strong></div>
                                                <div><Trans>Dátum</Trans>: <strong>{moment(v.from).format('DD. MM. YYYY HH:mm:ss')}</strong></div>
                                                <div><Trans>Stav</Trans>: <strong className="color_green">{numberWithSpaces(Number(v.count))}</strong></div>
                                            </div>
                                        </div>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                {showSum && (
                    <tfoot>
                        <tr>
                            <td style={{ borderTop: '2px solid #A81A40', fontWeight: 700, fontSize: '13px' }}>
                                <Trans>Priemer</Trans> (∅)
                            </td>
                            {columnAvgs.map((avg, i) => (
                                <td key={i} style={{ borderTop: '2px solid #A81A40', textAlign: 'center' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        {avg !== null ? numberWithSpaces(Math.round(avg)) : (
                                            <span style={{ color: '#999', fontStyle: 'italic', fontWeight: 400 }}>N/A</span>
                                        )}
                                    </strong>
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    )
}

export default OccupancyTableGraph
